// Pure helpers for InsertColumnMap — kept out of the component file to stay
// under the ~200-line budget. Shape gating (decision 4/5) is pure enough to
// unit-test without mounting React; the column lookup lives in
// `../update/assignmentColumns` (shared with the SET pickers).

import type { VqeEditContext } from '../../edit/editContext';
import { extractStructureForContext } from '../../edit/editContext';
import { useVqeStore } from '../../store/vqeStore';
import type { QueryStructure } from '../../../../sql-resolver/types/queryStructure';
import { vqeCopy } from '../../ux/vqeCopy';
import type { InsertBodyKind } from './extractInsertOutline';

export type ShapeGate = { readonly ok: true } | { readonly ok: false; readonly reason: string };

/** Column ops require an explicit, non-empty column list AND a VALUES body (decision 4). */
export function checkColumnOpsShape(
  columns: readonly string[] | null,
  bodyKind: InsertBodyKind | undefined,
): ShapeGate {
  const hasExplicitColumns = columns !== null && columns.length > 0;
  if (!hasExplicitColumns) {
    return { ok: false, reason: vqeCopy.disabled.insertColumnsNoExplicitList };
  }
  if (bodyKind === 'select') {
    return { ok: false, reason: vqeCopy.disabled.insertColumnsSourceQuery };
  }
  if (bodyKind !== 'values') {
    return { ok: false, reason: vqeCopy.disabled.editingUnavailable };
  }
  return { ok: true };
}

/** `extractStructureForContext` throws for a stale/synthetic statementId — degrade to undefined. */
export function safeExtractStructure(editContext: VqeEditContext): QueryStructure | undefined {
  try {
    return extractStructureForContext(editContext);
  } catch {
    return undefined;
  }
}

/** Shared failure-recording idiom for edit-action wrapper pre-validation throws (VqeFilterChipBuilder idiom). */
export function recordEditFailure(err: unknown): void {
  useVqeStore.getState().recordCommitFeedback({
    kind: 'failure',
    at: Date.now(),
    message: err instanceof Error ? err.message : String(err),
  });
}
